// The one upload path for anything that attaches a file to a Requirement:
// Service Desk ticket attachments, comment attachments, and External Portal
// Focus Item screenshots.
//
// Bytes go through Django (POST /api/media/), not browser-direct to S3. A
// browser PUT to a presigned S3 URL always triggers a CORS preflight, and the
// media bucket has no CORS configuration, so the preflight 403s and no bytes
// ever move. That was the whole of the "Network Error" failure — nothing
// reached S3's signature check.
//
// Two steps, the same shape EngagementMediaUploader has used in production for
// this problem (upload bytes, then create the row that references them):
//   1. POST /api/media/            -> { success, key, filename, content_type, size }
//   2. POST /api/requirement-media/ -> the RequirementMedia row
//
// object_type='requirement' + object_id=<requirement id> lands the object under
// <TENANT>/UPLOADED_MEDIA/requirement/<id>/, which is exactly the prefix
// MediaView.get already carves out of its tenant access check — so the file
// reads back through the same endpoint RequirementAttachmentList downloads from.

import axios from 'axios';
import { addAttachment } from '@/services/api/serviceDesk';
import * as Sentry from '@sentry/vue';

// Upload a File or Blob and attach it to a requirement. Resolves to the created
// RequirementMedia row. `filename` is required for a Blob (a File carries its
// own name) and MUST include a real extension — both MediaView and
// RequirementMediaViewSet.create gate on it.
export async function uploadRequirementMedia({
  requirementId,
  file,
  filename = null,
  onProgress = null,
}) {
  if (!file) throw new Error('No file to upload');
  if (!requirementId) {
    throw new Error('A requirement is required to attach a file');
  }

  const name = filename || file.name;

  try {
    // Step 1: bytes. Our own API, so the global axios instance is what we
    // want — it carries credentials, the CSRF token and tenant_slug.
    const formData = new FormData();
    formData.append('file', file, name);
    formData.append('object_type', 'requirement');
    formData.append('object_id', requirementId);

    const { data: uploaded } = await axios.post('/api/media/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? e => {
            if (e.total) onProgress(Math.round((e.loaded / e.total) * 100));
          }
        : undefined,
    });

    // MediaView reports validation failures as a 200-shaped body in some
    // paths and a 4xx in others, so check both.
    if (!uploaded?.success) {
      throw new Error(uploaded?.error || 'Upload failed');
    }

    // Step 2: the DB row.
    const media = await addAttachment({
      requirement: requirementId,
      file_key: uploaded.key,
      filename: name,
      file_type: uploaded.content_type || file.type || 'application/octet-stream',
      file_size: uploaded.size ?? file.size,
    });
    return media;
  } catch (err) {
    // Report the extension, never the filename — ticket attachments can carry
    // patient identifiers in their names.
    Sentry.captureException(err, {
      tags: { context: 'requirement_media_upload' },
      extra: {
        requirement_id: requirementId,
        extension: extensionOf(name),
        size: file.size,
        status: err?.response?.status ?? null,
      },
    });
    throw err;
  }
}

// The reason worth showing a user. /api/media/ returns real ones
// ("File size exceeds 50MB limit", "Unsupported file type", "Unsupported
// content type") — surface those rather than inventing copy.
export function uploadErrorMessage(err) {
  // nginx rejects an oversized body itself, before Django, and answers with an
  // HTML 413 rather than our JSON error shape.
  if (err?.response?.status === 413) {
    return 'File is too large to upload.';
  }
  return (
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.message ||
    'Upload failed'
  );
}

function extensionOf(name) {
  if (!name || !name.includes('.')) return '';
  return '.' + name.split('.').pop().toLowerCase();
}
